'use client'

import { faCalendar, faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { addMonths, differenceInDays, format, isSameDay } from "date-fns";


import ModalPage from '@/components/ui/Modal';
import { useOutsideClick } from '@/hooks/useOutsideClick';
import { usePopupPosition } from '@/hooks/usePopupPosition';
import useSizeController from '@/hooks/useSizeController';
import { AnimatePresence, motion } from 'framer-motion';
import React, { ReactNode, useEffect, useState } from "react";
import ScrollBox from './ScrollBox';
interface IDatePickerProps {
    maxRange?: number
    onChange: (values: { startDate: Date | null, endDate: Date | null }) => void,
    onSelected?: () => void;
    values: { startDate: Date | null, endDate: Date | null };
    singleDate?: boolean;
    showOneMonth?: boolean
}

export default function DatePicker({ onChange, onSelected, values, maxRange, singleDate, showOneMonth = false }: IDatePickerProps) {
    const [currentMonth, setCurrentMonth] = useState(values.startDate ? new Date(values.startDate) : new Date());
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);
    const [hoveredDate, setHoveredDate] = useState<Date | null>(null);

    useEffect(() => {
        setStartDate(values.startDate)
        setEndDate(values.endDate)
    }, [values.startDate?.toISOString(), values.endDate?.toISOString()])

    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
    const getDaysInMonth = (year: number, month: number) => {
        return new Array(31)
            .fill(null)
            .map((_, i) => new Date(year, month, i + 1))
            .filter((date) => date.getMonth() === month);
    };

    const isInRange = (date: Date) => {
        if (startDate && !endDate && hoveredDate) {
            return date > startDate && date < hoveredDate;
        }
        if (startDate && endDate) {
            return date > startDate && date < endDate;
        }
        return false;
    };

    const handleDateClick = (date: Date) => {
        if (!startDate || (startDate && endDate) || (date < startDate) || singleDate) {
            setStartDate(date);
            setEndDate(null);
            if (singleDate) {
                onChange({ startDate: date, endDate: null })
                onSelected && onSelected()
            }
        } else if (startDate && !endDate && date > startDate) {
            setEndDate(date);
            onChange({ startDate, endDate: date });
            onSelected && onSelected()
        } else if (startDate && (startDate.getTime() === date.getTime())) {
            setStartDate(null);
        }
    };

    const renderCalendar = (monthOffset: number, isFirst: boolean) => {

        const displayMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + monthOffset, 1);

        const daysInMonth = getDaysInMonth(displayMonth.getFullYear(), displayMonth.getMonth());
        const firstDayIndex = new Date(displayMonth.getFullYear(), displayMonth.getMonth(), 1).getDay();
        const today = new Date();

        return (
            <div className="flex flex-col p-2 sm:p-4 group 
                             rounded-lg relative !overflow-visible">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-3 sm:mb-6">
                    <div className="size-8">
                        <button
                            onClick={() => handleMonthChange(showOneMonth ? -1 : -2)}
                            className={`${showOneMonth ? 'block' : isFirst ? 'hidden md:block' : 'hidden'} size-8 rounded-lg text-secondary-500 aspect-square  
                                 bg-secondary-100 hover:text-white hover:bg-secondary-500 transition-colors duration-200`}
                        >
                            <FontAwesomeIcon icon={faChevronLeft} />
                        </button>
                    </div>
                    <span className="sm:text-lg font-semibold text-gray-800">
                        {displayMonth.toLocaleString("default", { month: "long" })} {displayMonth.getFullYear()}
                    </span>
                    <div className="size-8">
                        <button
                            onClick={() => handleMonthChange(showOneMonth ? 1 : 2)}
                            className={`${showOneMonth ? 'block' : isFirst ? 'hidden' : 'md:block hidden'} size-8 rounded-lg text-secondary-500 aspect-square  
                                 bg-secondary-100 hover:text-white hover:bg-secondary-500 transition-colors duration-200`}
                        >
                            <FontAwesomeIcon icon={faChevronRight} />
                        </button>
                    </div>
                </div>

                {/* Days of the Week */}
                <div className="grid overflow-x-visible grid-cols-7 gap-2 text-center text-sm font-medium text-gray-600 capitalize">
                    {days.map((day) => (
                        <div key={`${monthOffset}-${day}`} className="py-2 text-gray-500 tracking-wide">
                            {day.slice(0, 3)}
                        </div>
                    ))}
                </div>

                <div className="grid grid-cols-7 gap-2 mt-4 overflow-x-visible text-center">
                    {new Array(firstDayIndex).fill(null).map((_, i) => (
                        <div key={`empty-${monthOffset}-${i}`} className="bg-transparent"></div>
                    ))}

                    {daysInMonth.map((date) => {
                        const isStartSelected =
                            (startDate && isSameDay(date, startDate))
                        const isEndSelected = (endDate && isSameDay(date, endDate));

                        const isSelected = isStartSelected || isEndSelected

                        const inRange = !singleDate && isInRange(date);
                        const isToday = date.toDateString() === today.toDateString();
                        const isPastDate = date < today || !!(!!startDate && maxRange && !endDate && differenceInDays(date, startDate) > maxRange);

                        const isHoveredRange = !singleDate &&
                            hoveredDate &&
                            startDate &&
                            date >= startDate &&
                            date <= hoveredDate &&
                            !isSelected;

                        // Calculate number of nights
                        const nights =
                            startDate && hoveredDate
                                ? Math.max(0, Math.floor((hoveredDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)))
                                : 0;

                        const showNightsTag = !singleDate && startDate && hoveredDate && date.toDateString() === hoveredDate.toDateString();

                        const selectedRange = () => {
                            if (startDate && endDate) {
                                const nights = differenceInDays(endDate, startDate);
                                return nights; // Return the difference in days
                            }
                            return 0; // Return 0 if no valid range is selected
                        };

                        return (
                            <button
                                key={date.toISOString()}
                                className={`relative block aspect-square w-full group rounded-xl text-sm font-medium transition-all
                              ${isSelected
                                        ? "bg-secondary-500 text-white shadow-md scale-105"
                                        : inRange
                                            ? "bg-secondary-100 text-secondary-700"
                                            : isToday
                                                ? "border-2 border-secondary-300 text-secondary-700 bg-white"
                                                : (isHoveredRange && !endDate)
                                                    ? "bg-secondary-50 text-secondary-700"
                                                    : isPastDate
                                                        ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                                                        : "hover:bg-gray-100 text-gray-800"
                                    }`}
                                onClick={!isPastDate || isToday ? () => {
                                    handleDateClick(date);
                                } : undefined}
                                onMouseEnter={() => !isPastDate && setHoveredDate(date)}
                                disabled={isPastDate && !isToday} // Ensure today is never disabled
                            >
                                {date.getDate()}

                                {/* Today Tag */}
                                {isToday && (
                                    <span className="absolute top-0 right-0 bg-secondary-500 text-white text-xs px-2 py-1 rounded-full -translate-y-2 -translate-x-2 shadow">
                                        Today
                                    </span>
                                )}

                                {/* Nights Tag */}
                                {(showNightsTag && nights > 0 && !endDate) ? (
                                    <span className="absolute group-hover:opacity-100 opacity-0 min-w-max top-0 right-0 transition-opacity duration-200
                                     bg-secondary-700 text-white text-xs px-2 py-1 rounded-full -translate-y-2 -translate-x-2 shadow">
                                        {nights} Night{nights > 1 ? "s" : ""}
                                    </span>
                                ) : isEndSelected && <span className="absolute min-w-max top-0 right-0 
                                bg-white text-secondary-500 border border-secondary-500  text-xs px-2 py-1 rounded-full -translate-y-1/2 -translate-x-2 shadow">
                                    {selectedRange()} Night{selectedRange() > 1 ? "s" : ""}
                                </span>}
                            </button>

                        );
                    })}
                </div>
            </div>
        );
    };

    const handleMonthChange = (offset: number) => {
        const newMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + offset, 1);
        setCurrentMonth(newMonth);
    };

    const [mode, setMode] = useState<'dates' | 'flexible' | 'events'>('dates')
    const [duration, setDuration] = useState<'weekend' | 'week' | 'month'>('weekend')

    const [selectedMonth, setSelectedMonth] = useState<string>("");
    const [months, setMonths] = useState<{ name: string; year: number }[]>([]);
    const { isSmall } = useSizeController()
    useEffect(() => {
        const generateMonths = () => {
            const monthsArray = [];
            for (let i = 0; i < 6; i++) {
                const date = addMonths(new Date(), i);
                monthsArray.push({
                    name: format(date, "MMMM"),
                    year: date.getFullYear(),
                });
            }
            setMonths(monthsArray);
        };
        generateMonths();
    }, []);

    return (
        <div className={`w-full h-fit   overflow-x-visible md:w-auto
         ${showOneMonth ? 'md:min-w-80 ' : 'md:min-w-[45em]'} flex flex-col`}>
            {maxRange && <div className='text-secondary-500 text-center'>
                You can plan trips for up to {maxRange} days
            </div>}
            <AnimatePresence>
                {mode === 'dates' && <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    exit={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 100, y: 0 }}>

                    <div className={`grid ${showOneMonth ? 'grid-cols-1' : 'md:grid-cols-2'} overflow-x-visible gap-4 grow  max-h-full`}>

                        {renderCalendar(0, true)}
                        {!showOneMonth && <>
                            {
                                renderCalendar(1, false)
                            }
                            {
                                isSmall && <>
                                    {
                                        renderCalendar(2, false)}
                                    {
                                        renderCalendar(3, false)
                                    }
                                    {
                                        renderCalendar(4, false)
                                    }
                                </>
                            }
                        </>}
                    </div>
                </motion.div>}
            </AnimatePresence>
            <AnimatePresence>
                {mode === 'flexible' && <>
                    <motion.div
                        initial={{ opacity: 0, y: 5 }}
                        exit={{ opacity: 0, y: 5 }}
                        animate={{ opacity: 100, y: 0 }}
                        className='flex items-center h-full  justify-start flex-col w-full mt-8'>
                        <div className=' flex flex-col items-center gap-2'>
                            <p className='text-lg'>Stay for a <span className=''>{duration}</span> </p>
                            <div className='rounded-full text-neutral-800 text-sm font-semibold p-1 overflow-hidden grid bg-neutral-100 grid-cols-3 w-80 gap-2'>
                                <button onClick={() => setDuration('weekend')}
                                    className={`${duration === 'weekend' ? 'bg-white ' :
                                        ' hover:bg-neutral-200'}
                         w-full rounded-full py-2 transition-colors duration-200 text-center`}>
                                    Weekend
                                </button>
                                <button onClick={() => setDuration('week')}
                                    className={`${duration === 'week' ? 'bg-white ' :
                                        ' hover:bg-neutral-200'}
                         w-full rounded-full py-2 transition-colors duration-200 text-center`}>
                                    Week
                                </button>
                                <button onClick={() => setDuration('month')}
                                    className={`${duration === 'month' ? 'bg-white ' :
                                        ' hover:bg-neutral-200'}
                         w-full rounded-full py-2 transition-colors duration-200 text-center`}>
                                    Month
                                </button>
                            </div>
                        </div>
                        <div className=' flex flex-col items-center gap-2 w-11/12 text-neutral-800 mt-4'>
                            <p className='text-lg'>Go  {selectedMonth ? <span className=''>in {selectedMonth}</span> : <span>anytime</span>} </p>

                            <ScrollBox slidesToShowSmall={5} slidesToShowLarge={5}>
                                {months.map((month, index) => (
                                    <div className='px-1 pb-1 size-24 sm:size-28'
                                        key={index}
                                    >
                                        <div
                                            onClick={() => selectedMonth === month.name ? setSelectedMonth('') : setSelectedMonth(month.name)}
                                            className={`flex flex-col w-full h-full aspect-square justify-center items-center p-4 border rounded-lg cursor-pointer transition-all ${selectedMonth === month.name
                                                ? "border-black shadow-lg bg-neutral-50"
                                                : "border-gray-300"
                                                }`}
                                        >
                                            {/* Icon */}
                                            <FontAwesomeIcon
                                                icon={faCalendar}
                                                size='xl'
                                                className={`mb-2 text-sm sm:text-base ${selectedMonth === month.name ? "text-black" : "text-gray-500"
                                                    }`}
                                            />
                                            {/* Month Name */}
                                            <p
                                                className={`text-xs sm:text-base font-semibold ${selectedMonth === month.name ? "text-black" : "text-gray-700"}`}
                                            >
                                                {month.name}
                                            </p>
                                            {/* Year */}
                                            <p className="ext-xs sm:text-sm text-gray-500">{month.year}</p>
                                        </div>
                                    </div>
                                ))}
                            </ScrollBox>
                        </div>
                    </motion.div>
                </>}
            </AnimatePresence>
        </div>
    );
}

interface CalendarModalProps {
    maxRange?: number
    onClose?: (values: { startDate: Date | null, endDate: Date | null }) => void | Promise<void>;
    onChange: (values: { startDate: Date | null, endDate: Date | null }) => void;
    children?: ReactNode;
    applyButton?: ReactNode;
    containerClass?: string;
    buttonProps: React.HTMLAttributes<HTMLDivElement>;
    values: { startDate: Date | null, endDate: Date | null };
    singleDate?: boolean
    showOneMonth?: boolean
    preventAutoClose?: boolean
}

DatePicker.Modal = Modal

function Modal({ onClose, onChange, children, containerClass, preventAutoClose = false, applyButton, buttonProps, values, maxRange, singleDate, showOneMonth }: CalendarModalProps) {
    const { isSmall } = useSizeController();
    const inputRef = useOutsideClick(() => {

        !isSmall && setShow(false);
    });
    const [show, setShow] = useState(false);
    const position = usePopupPosition(inputRef, show, 200, 400);

    const handleChildClick = (e: any) => {
        e.stopPropagation()
        setShow((s) => !s);
    };


    return (<div
        {...buttonProps}
        ref={inputRef}
        onClick={handleChildClick}>
        {children}
        {!isSmall ? <AnimatePresence>
            {show && (
                <motion.div
                    onClick={(e) => e.stopPropagation()}
                    key="calendar"
                    className={`fixed md:absolute
                   ${position.vertical === 'top' ? 'top-0 md:top-auto md:bottom-[110%]' : 'top-0 md:top-[110%]'}
                   ${position.horizontal === 'left' ? ' left-0 ' : 'right-0'}
                  w-dvw h-dvh md:h-fit md:min-h-[400px] md:w-fit 
               `}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 5 }}
                    transition={{ duration: 0.2 }}
                    style={{ zIndex: 600000 }} // Ensure context is set
                >
                    <div
                        style={{ zIndex: 8000 }} // Ensure it's above the rotated div
                        className="flex h-full bg-white overflow-y-visible rounded shadow p-4">
                        <div className=''>
                            <DatePicker showOneMonth={showOneMonth} onSelected={() => {
                                if (singleDate) {
                                    setShow(false)
                                }
                            }}
                                singleDate={singleDate}
                                maxRange={maxRange}
                                values={values}
                                onChange={onChange} />
                        </div>
                    </div>
                </motion.div>)}
        </AnimatePresence> :
            <ModalPage appHeight='460px' isFull identifier='date-select-2' title='Select Date'
                onClose={() => {
                    setShow(false)
                }}
                show={show}>
                <div onClick={(e) => e.stopPropagation()} className='max-h-full overflow-x-visible overflow-y-auto'>
                    <DatePicker showOneMonth={showOneMonth} singleDate={singleDate} values={values} onChange={onChange}
                        onSelected={() => {
                            if (onClose) {
                                onClose(values)
                            }
                            !preventAutoClose && setShow(false)
                        }} />
                    {applyButton && <div className='mb-32 md:mb-0' onClick={() => setShow(false)}>
                        {applyButton}
                    </div>}
                </div>
            </ModalPage>
        }
    </div>
    )
}

export const DatePickerModal = Modal
